import { IComponent } from "../../../models/Interfaces";

export type HelpVisualType =
  | "accordion"
  | "hero"
  | "card"
  | "carousel"
  | "profile"
  | "embed"
  | "video"
  | "form"
  | "gallery"
  | "html"
  | "html-blocks"
  | "image"
  | "menu"
  | "news-article"
  | "news-summary"
  | "subscribe"
  | "social"
  | "tabs"
  | "text-block"
  | "generic";

export interface IComponentHelpMeta {
  title: string;
  summary: string;
  bestFor: string;
  visualType: HelpVisualType;
}

type HelpSeed = Omit<IComponentHelpMeta, "title">;

const helpByDisplayName: Record<string, HelpSeed> = {
  accordion: {
    summary: "Shows collapsible content panels where visitors open one item at a time.",
    bestFor: "FAQs, policy details, and content that should stay compact.",
    visualType: "accordion"
  },
  banner: {
    summary: "Displays a bold, full-width message with optional image and text styling.",
    bestFor: "Announcements, campaign highlights, or top-of-page messaging.",
    visualType: "hero"
  },
  card: {
    summary: "Presents content in a structured tile with heading, media, and supporting text.",
    bestFor: "Feature grids, services, and repeated content blocks.",
    visualType: "card"
  },
  carousel: {
    summary: "Rotates through multiple items in a single area using slides.",
    bestFor: "Hero rotations, testimonials, and featured content sets.",
    visualType: "carousel"
  },
  "contact profile": {
    summary: "Shows a contact or profile block with key identity and communication details.",
    bestFor: "Team pages, support contacts, and author highlights.",
    visualType: "profile"
  },
  "embedded object": {
    summary: "Embeds external interactive content such as maps, widgets, or iframes.",
    bestFor: "Third-party tools, booking widgets, and external embeds.",
    visualType: "embed"
  },
  "embedded video": {
    summary: "Embeds a hosted video player within page content.",
    bestFor: "Explainers, promos, tutorials, and recorded updates.",
    visualType: "video"
  },
  forms: {
    summary: "Renders an editable form block to collect visitor input.",
    bestFor: "Contact forms, lead capture, and request workflows.",
    visualType: "form"
  },
  gallery: {
    summary: "Displays multiple images in a visual collection layout.",
    bestFor: "Portfolios, case studies, event photos, and product imagery.",
    visualType: "gallery"
  },
  html: {
    summary: "Allows direct custom HTML content for advanced markup needs.",
    bestFor: "Special snippets, embed wrappers, and bespoke formatting.",
    visualType: "html"
  },
  "html blocks": {
    summary: "Manages multiple custom HTML content blocks in one component.",
    bestFor: "Stacked custom sections where each block has unique markup.",
    visualType: "html-blocks"
  },
  image: {
    summary: "Places a single image with optional classes and presentation controls.",
    bestFor: "Feature images, logos, and standalone visuals.",
    visualType: "image"
  },
  "menu bar": {
    summary: "Renders a navigation menu bar for quick route access.",
    bestFor: "Primary navigation, utility links, and section menus.",
    visualType: "menu"
  },
  "news article": {
    summary: "Displays a full article-style content block with richer detail.",
    bestFor: "News pages, announcements, and long-form updates.",
    visualType: "news-article"
  },
  "news summary": {
    summary: "Shows a rolling list of published pages marked as news for the configured summary URL.",
    bestFor: "News landing pages, update feeds, and article teaser sections.",
    visualType: "news-summary"
  },
  "register subscription": {
    summary: "Adds an opt-in block for visitor subscriptions or signups.",
    bestFor: "Newsletter joins, campaign signups, and mailing list growth.",
    visualType: "subscribe"
  },
  "social media block": {
    summary: "Displays social profile links or feed-style social content.",
    bestFor: "Cross-channel promotion and social engagement prompts.",
    visualType: "social"
  },
  "tabbed content": {
    summary: "Organizes related content into switchable tab panels.",
    bestFor: "Grouped product details, feature comparisons, and compact information sets.",
    visualType: "tabs"
  },
  "titled text block": {
    summary: "Shows a simple heading with supporting rich text content.",
    bestFor: "Section intros, explanatory copy, and editorial text blocks.",
    visualType: "text-block"
  }
};

const normalizeName = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

const fallbackHelp = (displayName: string): HelpSeed => ({
  summary: "Adds a reusable content block that can be configured after dropping it into a section.",
  bestFor: `When you need a ${displayName.toLowerCase()} style content element in your layout.`,
  visualType: "generic"
});

const inferHelpByKeywords = (displayName: string): HelpSeed | null => {
  const normalized = normalizeName(displayName);

  if (normalized.includes("accordion") || normalized.includes("faq")) return helpByDisplayName.accordion;
  if (normalized.includes("carousel") || normalized.includes("slider")) return helpByDisplayName.carousel;
  if (normalized.includes("gallery")) return helpByDisplayName.gallery;
  if (normalized.includes("video")) return helpByDisplayName["embedded video"];
  if (normalized.includes("image")) return helpByDisplayName.image;
  if (normalized.includes("tab")) return helpByDisplayName["tabbed content"];
  if (normalized.includes("form")) return helpByDisplayName.forms;
  if (normalized.includes("menu") || normalized.includes("nav")) return helpByDisplayName["menu bar"];
  if (normalized.includes("social")) return helpByDisplayName["social media block"];
  if (normalized.includes("html") || normalized.includes("embed")) return helpByDisplayName["embedded object"];
  if (normalized.includes("news") || normalized.includes("article")) return helpByDisplayName["news article"];
  if (normalized.includes("card")) return helpByDisplayName.card;
  if (normalized.includes("banner") || normalized.includes("hero")) return helpByDisplayName.banner;
  if (normalized.includes("text")) return helpByDisplayName["titled text block"];

  return null;
};

export const resolveComponentHelp = (component: IComponent): IComponentHelpMeta => {
  const title = component.DisplayName || component.Name || "Component";
  const normalized = normalizeName(title);
  const configured = helpByDisplayName[normalized];
  const inferred = inferHelpByKeywords(title);
  const selected = configured ?? inferred ?? fallbackHelp(title);

  return {
    title,
    summary: selected.summary,
    bestFor: selected.bestFor,
    visualType: selected.visualType
  };
};
